import React, { useEffect, useState } from 'react';
import { fetchLocation } from '@/apis';

const LocationFilter = ({ onLocationChange }) => {
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [isLocating, setIsLocating] = useState(false);

  // Fetch all states on initial mount
  useEffect(() => {
    const loadStates = async () => {
      try {
        const { data } = await fetchLocation('STATE', '98');
        setStatesList(data?.response || []);
      } catch (error) {
        console.error("Failed to load states:", error);
      }
    };
    loadStates();
  }, []);

  // Fetch cities (districts) when selectedState changes
  useEffect(() => {
    const loadCities = async () => {
      if (!selectedState) {
        setCitiesList([]);
        setSelectedCity("");
        return;
      }
      try {
        const { data } = await fetchLocation('DISTRICT', selectedState);
        setCitiesList(data?.response || []);
      } catch (error) {
        console.error("Failed to load districts:", error);
      }
    };
    loadCities();
  }, [selectedState]);

  // Notify parent whenever state or city changes
  useEffect(() => {
    onLocationChange({
      stateId: selectedState ? Number(selectedState) : null,
      districtId: selectedCity ? Number(selectedCity) : null
    });
  }, [selectedState, selectedCity]);

  // Geolocation and OSM Nominatim API reverse geocoding
  const handleNearMeClick = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`, {
            headers: {
              'User-Agent': 'KisanGatewayApp/1.0'
            }
          });
          const data = await res.json();
          const address = data?.address;

          if (!address) {
            throw new Error("No address returned from geocoder");
          }

          const geoState = address.state || "";
          const geoDistrict = address.state_district || address.city || address.county || "";

          if (geoState) {
            // Find match in statesList
            const matchedState = statesList.find(s => 
              s.name.toLowerCase() === geoState.toLowerCase() ||
              geoState.toLowerCase().includes(s.name.toLowerCase()) ||
              s.name.toLowerCase().includes(geoState.toLowerCase())
            );

            if (matchedState) {
              setSelectedState(matchedState.id);

              // Load districts for matching
              const distRes = await fetchLocation('DISTRICT', matchedState.id);
              const stateDistricts = distRes?.data?.response || [];
              setCitiesList(stateDistricts);

              if (geoDistrict) {
                const matchedDist = stateDistricts.find(d => 
                  d.name.toLowerCase() === geoDistrict.toLowerCase() ||
                  geoDistrict.toLowerCase().includes(d.name.toLowerCase()) ||
                  d.name.toLowerCase().includes(geoDistrict.toLowerCase())
                );

                if (matchedDist) {
                  setSelectedCity(matchedDist.id);
                } else {
                  setSelectedCity("");
                }
              } else {
                setSelectedCity("");
              }
            } else {
              alert(`Detected location in ${geoState}, but it couldn't be matched to our state list.`);
            }
          } else {
            alert("Could not detect state from location coordinates.");
          }
        } catch (error) {
          console.error("Reverse geocoding error:", error);
          alert("Error detecting location details. Please select manually.");
        } finally {
          setIsLocating(false);
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        alert("Location permission denied or unavailable. Please select state and city manually.");
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleClearFilters = () => {
    setSelectedState("");
    setSelectedCity("");
    setCitiesList([]);
  };

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap justify-content-md-end">
      <style>{`
        @keyframes spin-anim {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotate-animation {
          animation: spin-anim 1s linear infinite;
          display: inline-block;
        }
      `}</style>

      {/* State Selector */}
      <div className="d-flex align-items-center bg-white border rounded-pill px-3 py-1 shadow-sm gap-2" style={{ borderColor: '#d1d5db', height: '42px', minWidth: '180px' }}>
        <i className="bi bi-map text-secondary" style={{ fontSize: '14px' }}></i>
        <select
          className="form-select border-0 bg-transparent shadow-none p-0 fw-semibold text-secondary"
          style={{ fontSize: '13px', cursor: 'pointer', width: '130px', outline: 'none' }}
          value={selectedState}
          onChange={(e) => {
            setSelectedState(e.target.value);
            setSelectedCity("");
          }}
        >
          <option value="">Select State</option>
          {statesList.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {/* City Selector */}
      <div className="d-flex align-items-center bg-white border rounded-pill px-3 py-1 shadow-sm gap-2" style={{ borderColor: '#d1d5db', height: '42px', minWidth: '180px', opacity: selectedState ? 1 : 0.7 }}>
        <i className="bi bi-geo text-secondary" style={{ fontSize: '14px' }}></i>
        <select
          className="form-select border-0 bg-transparent shadow-none p-0 fw-semibold text-secondary"
          style={{ fontSize: '13px', cursor: selectedState ? 'pointer' : 'not-allowed', width: '130px', outline: 'none' }}
          value={selectedCity}
          onChange={(e) => setSelectedCity(e.target.value)}
          disabled={!selectedState}
        >
          <option value="">Select City</option>
          {citiesList.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {/* Near Me Button */}
      <button
        type="button"
        className="btn d-flex align-items-center gap-2 px-3 py-1 rounded-pill shadow-sm border-0 text-white transition-all"
        style={{
          fontSize: '13px',
          fontWeight: '600',
          height: '42px',
          backgroundColor: isLocating ? '#6c757d' : 'var(--kg-green, #28a745)',
          transition: 'all 0.3s ease',
        }}
        onClick={handleNearMeClick}
        disabled={isLocating}
      >
        <i className={`bi ${isLocating ? 'bi-arrow-repeat rotate-animation' : 'bi-geo-alt-fill'}`}></i>
        {isLocating ? 'Locating...' : 'Near Me'}
      </button>

      {/* Reset / Clear Button */}
      {(selectedState || selectedCity) && (
        <button
          type="button"
          className="btn btn-outline-danger btn-sm rounded-pill px-3 fw-bold d-flex align-items-center justify-content-center"
          style={{ fontSize: '12px', height: '42px' }}
          onClick={handleClearFilters}
        >
          Reset
        </button>
      )}
    </div>
  );
};

export default LocationFilter;
